// useLocation is safe for files not rendered in a partial on the overview.

import {type BlobRange, parseFileAnchor} from '@github-ui/blob-anchor'
import {hpcAttrs} from '@github-ui/hpc'
// eslint-disable-next-line no-restricted-imports
import {useLocation} from '@github-ui/react-router'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useWindowVirtualizer} from '@github-ui/virtualizer'
import {SearchIcon} from '@primer/octicons-react'
import {TextInput} from '@primer/react'
import {ScrollableRegion} from '@primer/react/experimental'
import {clsx} from 'clsx'
import React, {useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import {isLineInViewport} from '../../../../utilities/lines'
import {
  hasHiddenUnicodeCharacters,
  HiddenUnicodeAlert,
  isHiddenUnicodeShown,
  showHiddenUnicodeCharacters,
} from '../../Banners/HiddenUnicode'
import type {CodeLinesHandle} from '../Code/code-lines-handle'
import {HighlightedLinesProvider, useHighlightedLinesInfo} from '../Code/HighlightedLineProvider'
import {LineNumber} from '../Code/LineNumber'
import {HighlightedLineMenuContainer} from '../HighlightedLineMenu'
import styles from './CSVBlob.module.css'

interface CSVRowData {
  lineNumber: number
  cells: React.ReactNode[]
  highlighted: boolean
  hiddenUnicode: boolean
}

export default function CSVBlob({csv}: {csv: SafeHTMLString[][]}) {
  const tableHandle = useRef<CSVTableHandle>(null)
  const [highlightedLines, setHighlightedLines] = useHighlightedLines()
  const csvRows = toCSVData(csv, highlightedLines)
  const showHiddenUnicodeAlert = csvRows.some(row => row.hiddenUnicode)
  const {anchorPrefix} = parseFileAnchor(useLocation().hash)
  const onLineNumberClick = useCallback(() => {
    setHighlightedLines(parseFileAnchor(window.location.hash)?.blobRange)
  }, [setHighlightedLines])

  useEffect(() => {
    const anchorInfo = parseFileAnchor(window.location.hash)
    const table = tableHandle.current
    if (!highlightedLines || !table) return

    if (isLineInViewport(anchorInfo.blobRange.start.line)) return

    if (highlightedLines.start.line < 10) {
      // If the line is near the top, scroll to the top of the sticky thing
      tableHandle.current?.scrollToTop()
    } else {
      tableHandle.current?.scrollToLine(anchorInfo.blobRange.start.line)
    }
  }, [highlightedLines, anchorPrefix])

  return (
    <HighlightedLinesProvider highlightedLines={highlightedLines}>
      <CSVFilter onFilterChange={query => tableHandle.current?.setFilter(query)} />
      {showHiddenUnicodeAlert && <HiddenUnicodeAlert />}
      <HighlightedLineMenuContainer>
        <CSVTable ref={tableHandle} csvRows={csvRows} onLineNumberClick={onLineNumberClick} />
      </HighlightedLineMenuContainer>
    </HighlightedLinesProvider>
  )
}

function CSVFilter({onFilterChange}: {onFilterChange: (value: string) => void}) {
  return (
    <div className={styles.csvFilter}>
      <TextInput
        aria-label="Search this file"
        autoCapitalize="off"
        leadingVisual={SearchIcon}
        onChange={e => {
          const query = e.target.value.toLowerCase()
          onFilterChange(query)
        }}
        block
        name="filter"
        placeholder="Search this file"
        type="text"
      />
    </div>
  )
}

interface CSVTableHandle extends CodeLinesHandle {
  setFilter: (query: string) => void
}

interface CSVTableProps {
  csvRows: readonly CSVRowData[]
  onLineNumberClick: React.MouseEventHandler<HTMLDivElement>
  ref?: React.Ref<CSVTableHandle>
}

function CSVTable({csvRows, onLineNumberClick, ref}: CSVTableProps) {
  const [filter, setFilter] = useState<string | null>(null)
  const parentRef = useRef<HTMLTableElement>(null)
  const currentLineHeight = useCurrentLineHeight('react-csv-table-body', 35)

  const filteredCSVRows = useMemo(
    () =>
      filter
        ? csvRows.slice(1).filter(row => row.cells.join(' ').toLowerCase().includes(filter.toLowerCase()))
        : csvRows.slice(1),
    [csvRows, filter],
  )

  // Track parent offset for scrollMargin calculation
  const [parentOffset, setParentOffset] = useState(0)

  useLayoutEffect(() => {
    setParentOffset(parentRef.current?.offsetTop ?? 0)
  }, [])

  const {items, totalSize, scrollToIndex} = useWindowVirtualizer({
    count: filteredCSVRows.length,
    overscan: 200,
    estimateSize: useCallback(() => currentLineHeight, [currentLineHeight]),
    scrollMargin: parentOffset,
  })

  useImperativeHandle(ref, () => ({
    scrollToTop: () => scrollToIndex(0, {align: 'start'}),
    scrollToLine: lineNumber => {
      const index = filteredCSVRows.findIndex(row => row.lineNumber === lineNumber)
      scrollToIndex(index, {align: 'start'})
    },
    setFilter,
  }))

  return (
    <ScrollableRegion aria-labelledby="LC1">
      <table
        ref={parentRef}
        className={styles.csvTable}
        // Add the headers height to the total size
        style={{
          height: totalSize + currentLineHeight,
          paddingTop: `${items[0]?.start ?? 0}px`,
        }}
        {...hpcAttrs()}
        data-testid="csv-table"
      >
        {csvRows.length > 0 && (
          <>
            <CSVHeader
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              hiddenUnicode={csvRows[0]!.hiddenUnicode}
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              cells={csvRows[0]!.cells}
              onLineNumberClick={onLineNumberClick}
            />
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            <tbody className="react-csv-table-body">
              {items.map(virtualItem => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const {cells, hiddenUnicode, lineNumber} = filteredCSVRows[virtualItem.index]!

                return (
                  <CSVRow
                    key={lineNumber}
                    lineNumber={lineNumber}
                    cells={cells}
                    hiddenUnicode={hiddenUnicode}
                    onLineNumberClick={onLineNumberClick}
                  />
                )
              })}
            </tbody>
          </>
        )}
      </table>
    </ScrollableRegion>
  )
}

const CSVHeader = React.memo(CSVHeaderUnmemoized)

function CSVHeaderUnmemoized({
  cells,
  hiddenUnicode,
  onLineNumberClick,
}: {
  cells: React.ReactNode[]
  hiddenUnicode: boolean
  onLineNumberClick: React.MouseEventHandler<HTMLDivElement>
}) {
  return (
    <thead>
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <tr id={`LC1`} className="react-csv-row">
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <th className="react-csv-line-number">
          <LineNumber codeLineData={{lineNumber: 1, hiddenUnicode}} onClick={onLineNumberClick} />
        </th>
        {cells.map((cellContents, columnIndex) => {
          return (
            // eslint-disable-next-line @eslint-react/no-array-index-key, @github-ui/github-monorepo/no-global-classname-literals
            <th className="react-csv-cell react-csv-cell--header" key={`0-${columnIndex}`}>
              {cellContents}
            </th>
          )
        })}
      </tr>
    </thead>
  )
}

const CSVRow = React.memo(CSVRowUnmemoized)

function CSVRowUnmemoized({
  lineNumber,
  cells,
  hiddenUnicode,
  onLineNumberClick,
}: {
  lineNumber: number
  cells: React.ReactNode[]
  hiddenUnicode: boolean
  onLineNumberClick: React.MouseEventHandler<HTMLDivElement>
}) {
  const highlightedLines = useHighlightedLinesInfo(lineNumber)
  const highlighted =
    !!highlightedLines && lineNumber >= highlightedLines.start.line && lineNumber <= highlightedLines.end.line
  const className = clsx('react-csv-row', highlighted && 'react-csv-row--highlighted')

  return (
    <tr id={`LC${lineNumber}`} className={className}>
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <td className="react-csv-line-number">
        <LineNumber codeLineData={{lineNumber, hiddenUnicode}} onClick={onLineNumberClick} />
      </td>
      {cells.map((cellContents, columnIndex) => {
        return (
          // eslint-disable-next-line @eslint-react/no-array-index-key, @github-ui/github-monorepo/no-global-classname-literals
          <td key={`${lineNumber}-${columnIndex}`} className="react-csv-cell">
            {cellContents}
          </td>
        )
      })}
    </tr>
  )
}

function toCSVData(csv: SafeHTMLString[][], highlightedLines: BlobRange | undefined): CSVRowData[] {
  return csv.map((row, rowIndex) => {
    const hiddenUnicodeShown = isHiddenUnicodeShown()
    const hiddenUnicode = hasHiddenUnicodeCharacters(row.join(''))
    const cells =
      hiddenUnicodeShown && hiddenUnicode
        ? row.map(cell => (cell ? (showHiddenUnicodeCharacters(cell) ?? cell) : cell))
        : row

    const lineNumber = rowIndex + 1
    const highlighted =
      !!highlightedLines && lineNumber >= highlightedLines.start.line && lineNumber <= highlightedLines.end.line

    return {lineNumber, cells, hiddenUnicode, highlighted}
  })
}

function useHighlightedLines(): [BlobRange | undefined, React.Dispatch<React.SetStateAction<BlobRange | undefined>>] {
  const {hash} = useLocation()
  const initialHighlightedLines = parseFileAnchor(hash).blobRange
  const highlightedLinesRef = useRef<BlobRange | undefined>(initialHighlightedLines)
  const [highlightedLines, setHighlightedLines] = useState<BlobRange | undefined>(initialHighlightedLines)

  useEffect(() => {
    const newBlobRange = parseFileAnchor(window.location.hash).blobRange

    if (
      newBlobRange?.start.line !== highlightedLinesRef.current?.start.line ||
      newBlobRange?.end.line !== highlightedLinesRef.current?.end.line
    ) {
      highlightedLinesRef.current = newBlobRange

      setHighlightedLines(newBlobRange)
    }
  }, [hash])

  return [highlightedLines, setHighlightedLines]
}
